// src/pages/finance-portal/requisitions/RequisitionForm.js
//
// Create/Edit a Purchase Requisition (full-page).
// New: POST /api/finance/requisitions
// Edit: PUT /api/finance/requisitions/:id (only when DRAFT or REJECTED)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField, IconButton, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    MenuItem, Select, FormControl, InputLabel, Alert, CircularProgress,
    Autocomplete, Skeleton, InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const URGENCY_OPTIONS = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

function emptyLine() {
    return { description: '', quantity: 1, unit_of_measure: '',
             estimated_cost: '', gl_account_id: '', line_notes: '' };
}

export default function RequisitionForm() {
    const navigate = useNavigate();
    const { requisitionId } = useParams();
    const isEdit = !!requisitionId;
    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState({
        department: '',
        needed_by_date: '',
        urgency: 'NORMAL',
        justification: '',
        preferred_vendor_id: '',
        currency: 'USD',
        notes: '',
    });
    const [lines, setLines] = useState([emptyLine()]);
    const [vendors, setVendors] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fetchVendors = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/vendors',
                { params: { per_page: 200, status: 'ACTIVE' } });
            if (res.data.success) setVendors(res.data.data || []);
        } catch { /* silent */ }
    }, []);

    const fetchGlAccounts = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
            if (res.data.success) setGlAccounts(res.data.data || []);
        } catch { /* silent */ }
    }, []);

    const fetchExisting = useCallback(async () => {
        if (!isEdit) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/requisitions/${requisitionId}`);
            if (res.data.success) {
                const r = res.data.data;
                setData({
                    department: r.department || '',
                    needed_by_date: r.needed_by_date || '',
                    urgency: r.urgency || 'NORMAL',
                    justification: r.justification || '',
                    preferred_vendor_id: r.preferred_vendor_id || '',
                    currency: r.currency || 'USD',
                    notes: r.notes || '',
                });
                setLines((r.lines || []).map(l => ({
                    description: l.description || '',
                    quantity: String(l.quantity ?? 1),
                    unit_of_measure: l.unit_of_measure || '',
                    estimated_cost: String(l.estimated_cost ?? ''),
                    gl_account_id: l.gl_account_id || '',
                    line_notes: l.line_notes || '',
                })));
                if (r.status !== 'DRAFT' && r.status !== 'REJECTED') {
                    setError(`This requisition is in ${r.status} status and cannot be edited.`);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load requisition');
        } finally {
            setLoading(false);
        }
    }, [isEdit, requisitionId]);

    useEffect(() => { fetchVendors(); fetchGlAccounts(); fetchExisting(); },
              [fetchVendors, fetchGlAccounts, fetchExisting]);

    const update = (k, v) => setData(prev => ({ ...prev, [k]: v }));
    const updateLine = (i, k, v) => setLines(prev => prev.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
    const addLine = () => setLines(prev => [...prev, emptyLine()]);
    const removeLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));

    const totalEst = lines.reduce((s, l) =>
        s + (parseFloat(l.quantity) || 0) * (parseFloat(l.estimated_cost) || 0), 0);

    const validateLocal = () => {
        const e = {};
        if (lines.length === 0) e.lines = 'At least one line is required';
        lines.forEach((l, i) => {
            if (!l.description?.trim()) e[`lines[${i}].description`] = 'Required';
            const q = parseFloat(l.quantity);
            if (isNaN(q) || q <= 0) e[`lines[${i}].quantity`] = 'Must be > 0';
            const c = parseFloat(l.estimated_cost);
            if (isNaN(c) || c < 0) e[`lines[${i}].estimated_cost`] = 'Must be ≥ 0';
        });
        return e;
    };

    const handleSave = async () => {
        const localErrors = validateLocal();
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            enqueueSnackbar('Fix the highlighted errors', { variant: 'warning' });
            return;
        }
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                department: data.department || null,
                needed_by_date: data.needed_by_date || null,
                urgency: data.urgency,
                justification: data.justification || null,
                preferred_vendor_id: data.preferred_vendor_id || null,
                currency: data.currency || 'USD',
                notes: data.notes || null,
                lines: lines.map(l => ({
                    description: l.description.trim(),
                    quantity: parseFloat(l.quantity),
                    unit_of_measure: l.unit_of_measure || null,
                    estimated_cost: parseFloat(l.estimated_cost),
                    gl_account_id: l.gl_account_id || null,
                    line_notes: l.line_notes || null,
                })),
            };
            let id = requisitionId;
            if (isEdit) {
                await axiosInstance.put(`/api/finance/requisitions/${requisitionId}`, payload);
                enqueueSnackbar('Requisition updated', { variant: 'success' });
            } else {
                const res = await axiosInstance.post('/api/finance/requisitions', payload);
                id = res.data?.data?.id;
                enqueueSnackbar('Requisition created', { variant: 'success' });
            }
            navigate(`/finance-portal/requisitions/${id}`);
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                enqueueSnackbar('Validation failed — see line errors', { variant: 'warning' });
            } else {
                enqueueSnackbar(err.response?.data?.message || 'Failed to save', { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };
    const labelSx = { fontFamily, fontSize: '0.85rem' };
    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
                        textTransform: 'uppercase', bgcolor: '#f8fafc',
                        borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' };

    if (loading) {
        return <Box sx={{ p: 4, fontFamily }}><Skeleton height={400} /></Box>;
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/requisitions')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            Purchase Requisition
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {isEdit ? 'Edit Requisition' : 'New Requisition'}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Requisition Details
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                              gap: 1.5 }}>
                        <TextField label="Department" size="small"
                                   value={data.department}
                                   onChange={e => update('department', e.target.value)}
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Urgency</InputLabel>
                            <Select value={data.urgency} label="Urgency"
                                    onChange={e => update('urgency', e.target.value)}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {URGENCY_OPTIONS.map(o =>
                                    <MenuItem key={o} value={o} sx={{ fontFamily }}>{o}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Needed By" type="date" size="small"
                                   value={data.needed_by_date}
                                   onChange={e => update('needed_by_date', e.target.value)}
                                   InputLabelProps={{ ...labelSx, shrink: true, sx: labelSx }}
                                   InputProps={{ sx: fieldSx }} />
                        <Autocomplete size="small"
                                      options={vendors}
                                      getOptionLabel={v => v ? (v.display_name || v.company_name || `Vendor #${v.id}`) : ''}
                                      value={vendors.find(v => v.id === parseInt(data.preferred_vendor_id, 10)) || null}
                                      onChange={(_, val) => update('preferred_vendor_id', val?.id || '')}
                                      renderInput={params =>
                                          <TextField {...params} label="Preferred Vendor"
                                                     InputLabelProps={{ ...params.InputLabelProps, sx: labelSx }}
                                                     InputProps={{ ...params.InputProps,
                                                                   sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' } }} />} />
                        <TextField label="Currency" size="small" sx={{ width: 120 }}
                                   value={data.currency}
                                   onChange={e => update('currency', e.target.value.toUpperCase())}
                                   inputProps={{ maxLength: 3 }}
                                   InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    </Box>
                    <TextField label="Justification" size="small" fullWidth multiline rows={2} sx={{ mt: 1.5 }}
                               value={data.justification}
                               onChange={e => update('justification', e.target.value)}
                               InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                    <TextField label="Notes" size="small" fullWidth multiline rows={2} sx={{ mt: 1.5 }}
                               value={data.notes}
                               onChange={e => update('notes', e.target.value)}
                               InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }} />
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0',
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>Items</Typography>
                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem' }}>
                                Estimated Total:{' '}
                                <strong style={{ color: '#1e293b' }}>
                                    {new Intl.NumberFormat('en-US',
                                        { style: 'currency', currency: data.currency || 'USD' }).format(totalEst)}
                                </strong>
                            </Typography>
                        </Box>
                        <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={addLine}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                            Add Line
                        </Button>
                    </Box>
                    {errors.lines && <Box sx={{ p: 2 }}>
                        <Alert severity="warning" sx={{ borderRadius: '10px', fontFamily }}>{errors.lines}</Alert>
                    </Box>}
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={labelCell}>Description</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 100 }}>Qty</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 100 }}>UoM</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 150 }}>Est. Unit Cost</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 280 }}>GL Account</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 200 }}>Notes</TableCell>
                                    <TableCell sx={{ ...labelCell, width: 56 }}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lines.map((l, i) => (
                                    <TableRow key={i}>
                                        <TableCell>
                                            <TextField size="small" fullWidth value={l.description}
                                                       onChange={e => updateLine(i, 'description', e.target.value)}
                                                       error={!!errors[`lines[${i}].description`]}
                                                       helperText={errors[`lines[${i}].description`] || ''}
                                                       InputProps={{ sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" type="number" fullWidth value={l.quantity}
                                                       onChange={e => updateLine(i, 'quantity', e.target.value)}
                                                       error={!!errors[`lines[${i}].quantity`]}
                                                       helperText={errors[`lines[${i}].quantity`] || ''}
                                                       InputProps={{ sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" fullWidth value={l.unit_of_measure}
                                                       onChange={e => updateLine(i, 'unit_of_measure', e.target.value)}
                                                       InputProps={{ sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" type="number" fullWidth value={l.estimated_cost}
                                                       onChange={e => updateLine(i, 'estimated_cost', e.target.value)}
                                                       error={!!errors[`lines[${i}].estimated_cost`]}
                                                       helperText={errors[`lines[${i}].estimated_cost`] || ''}
                                                       InputProps={{
                                                           startAdornment: <InputAdornment position="start"
                                                               sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.82rem' } }}>
                                                               $
                                                           </InputAdornment>,
                                                           sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            <Autocomplete size="small"
                                                          options={glAccounts}
                                                          getOptionLabel={a => a ? `${a.account_number} — ${a.name}` : ''}
                                                          value={glAccounts.find(a => a.id === parseInt(l.gl_account_id, 10)) || null}
                                                          onChange={(_, val) => updateLine(i, 'gl_account_id', val?.id || '')}
                                                          renderInput={p => <TextField {...p} placeholder="Optional"
                                                                                       InputProps={{ ...p.InputProps,
                                                                                                     sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size="small" fullWidth value={l.line_notes}
                                                       onChange={e => updateLine(i, 'line_notes', e.target.value)}
                                                       InputProps={{ sx: { fontFamily, fontSize: '0.82rem', borderRadius: '8px' } }} />
                                        </TableCell>
                                        <TableCell>
                                            {lines.length > 1 && (
                                                <IconButton size="small" onClick={() => removeLine(i)}
                                                            sx={{ color: '#ef4444' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button onClick={() => navigate('/finance-portal/requisitions')}
                            sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving || !!error}
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                  borderRadius: '10px', bgcolor: ACCENT,
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Requisition'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
